import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Button, Chip, IconButton, Tab, Tabs, Tooltip } from "@mui/material";
import { eventClick, RefreshData } from "components/helper";
import DeleteModal from "components/Modals/DeleteModal";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useMemo, useState } from "react";
import { AiFillDelete, AiFillEdit, AiFillEye } from "react-icons/ai";
import { FaPlusCircle } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useDeleteEmployee,
  useEmployees,
  useTimesheets,
  useTimesheetSummary,
  useUpdateTimesheetPayment,
} from "utils/employeeService";
import {
  employeeClockInUrl,
  entryDurationMinutes,
  formatDuration,
  isInCurrentWeek,
  isOpenEntry,
  normalizePaymentStatus,
  PAYMENT_STATUS,
} from "utils/timesheetUtils";
import TimesheetEntriesTable from "./TimesheetEntriesTable";
import TimesheetStatCards from "./TimesheetStatCards";
import TimesheetWorkingTodayTable from "./TimesheetWorkingTodayTable";

const Timesheets = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [openConfirmationModal, setOpenConfirmationModal] = useState({});
  const clockInUrl = employeeClockInUrl();

  const {
    data: employees = [],
    isLoading: employeesLoading,
    refetch: refetchEmployees,
  } = useEmployees();
  const {
    data: timesheetPage,
    isLoading: entriesLoading,
    refetch: refetchTimesheets,
  } = useTimesheets({ page: 0, size: 500, sortBy: "clockIn", sortDirection: "DESC" });
  const { data: remoteSummary, refetch: refetchSummary } = useTimesheetSummary();

  const deleteEmployeeMutation = useDeleteEmployee({
    onSuccess: () => {
      setOpenConfirmationModal({ state: false, id: null });
      toast.success("Employee deleted successfully");
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.errorMessage ||
          error?.response?.data?.message ||
          "Error deleting employee"
      );
    },
  });

  const updatePaymentMutation = useUpdateTimesheetPayment({
    onSuccess: () => toast.success("Payment status updated"),
    onError: (error) =>
      toast.error(error?.response?.data?.errorMessage || "Failed to update payment status"),
  });

  const entries = timesheetPage?.content || [];

  const workingToday = useMemo(
    () =>
      entries
        .filter((entry) => isOpenEntry(entry))
        .map((entry) => {
          const employee = (employees || []).find(
            (item) => String(item.id) === String(entry.employeeId || entry.employee?.id)
          );
          return {
            ...entry,
            employeeName: entry.employeeName || employee?.name || "-",
            department: employee?.department || entry.department || "-",
            jobTitle: employee?.jobTitle || entry.jobTitle || "-",
          };
        })
        .sort(
          (a, b) =>
            new Date(a.clockIn || a.clockInAt || 0) - new Date(b.clockIn || b.clockInAt || 0)
        ),
    [employees, entries]
  );

  const employeesWithStats = useMemo(
    () =>
      (employees || []).map((employee) => {
        const employeeEntries = entries.filter(
          (entry) => String(entry.employeeId || entry.employee?.id) === String(employee.id)
        );
        const weekMinutes = employeeEntries
          .filter((entry) => isInCurrentWeek(entry.clockIn || entry.clockInAt))
          .reduce((sum, entry) => sum + entryDurationMinutes(entry), 0);
        const unpaidEntries = employeeEntries.filter(
          (entry) =>
            !isOpenEntry(entry) &&
            normalizePaymentStatus(entry.paymentStatus ?? entry.paidStatus) ===
              PAYMENT_STATUS.UNPAID
        );
        return {
          ...employee,
          weekMinutes: employee.weekMinutes ?? weekMinutes,
          unpaidMinutes:
            employee.unpaidMinutes ??
            unpaidEntries.reduce((sum, entry) => sum + entryDurationMinutes(entry), 0),
          unpaidCount: unpaidEntries.length,
          clockedIn:
            employee.isClockedIn ||
            employee.clockedIn ||
            employeeEntries.some((entry) => isOpenEntry(entry)),
        };
      }),
    [employees, entries]
  );

  const computedSummary = useMemo(() => {
    const unpaidEntries = entries.filter(
      (entry) =>
        !isOpenEntry(entry) &&
        normalizePaymentStatus(entry.paymentStatus ?? entry.paidStatus) === PAYMENT_STATUS.UNPAID
    );
    return {
      employeeCount: remoteSummary?.employeeCount ?? employeesWithStats.length,
      clockedInCount:
        remoteSummary?.clockedInCount ?? employeesWithStats.filter((item) => item.clockedIn).length,
      weekMinutes:
        remoteSummary?.weekMinutes ??
        entries
          .filter((entry) => isInCurrentWeek(entry.clockIn || entry.clockInAt))
          .reduce((sum, entry) => sum + entryDurationMinutes(entry), 0),
      unpaidMinutes:
        remoteSummary?.unpaidMinutes ??
        unpaidEntries.reduce((sum, entry) => sum + entryDurationMinutes(entry), 0),
      unpaidCount: remoteSummary?.unpaidCount ?? unpaidEntries.length,
    };
  }, [employeesWithStats, entries, remoteSummary]);

  const refreshAll = () => {
    refetchEmployees();
    refetchTimesheets();
    refetchSummary();
  };

  const copyClockInLink = async () => {
    try {
      await navigator.clipboard.writeText(clockInUrl);
      toast.success("Clock-in link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 180,
        Cell: ({ renderedCellValue }) => (
          <span className="font-semibold text-app-text-secondary">{renderedCellValue || "-"}</span>
        ),
      },
      {
        id: "organisation",
        header: "Organisation",
        size: 180,
        accessorFn: (row) => row.orgName || row.organization?.name || row.organisation?.name || "-",
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 200,
        Cell: ({ renderedCellValue }) => renderedCellValue || "-",
      },
      {
        accessorKey: "contact",
        header: "Contact",
        size: 130,
        Cell: ({ renderedCellValue }) => renderedCellValue || "-",
      },
      {
        accessorKey: "department",
        header: "Department",
        size: 140,
        Cell: ({ renderedCellValue }) => renderedCellValue || "-",
      },
      {
        accessorKey: "jobTitle",
        header: "Job title",
        size: 140,
        Cell: ({ renderedCellValue }) => renderedCellValue || "-",
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 110,
        Cell: ({ renderedCellValue }) => {
          const active = renderedCellValue === "ACTIVE" || renderedCellValue === true;
          return (
            <Chip
              size="small"
              label={active ? "Active" : "Inactive"}
              color={active ? "success" : "default"}
              variant="outlined"
            />
          );
        },
      },
      {
        accessorKey: "clockedIn",
        header: "Shift",
        size: 130,
        Cell: ({ row }) =>
          row.original.clockedIn ? (
            <Chip size="small" color="success" label="Clocked in" />
          ) : (
            <Chip size="small" variant="outlined" label="Clocked out" />
          ),
      },
      {
        accessorKey: "weekMinutes",
        header: "This week",
        size: 110,
        Cell: ({ row }) => formatDuration(row.original.weekMinutes),
      },
      {
        accessorKey: "unpaidMinutes",
        header: "Unpaid",
        size: 110,
        Cell: ({ row }) => (
          <span className={row.original.unpaidMinutes ? "font-semibold text-amber-700" : ""}>
            {formatDuration(row.original.unpaidMinutes)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        size: 140,
        enableSorting: false,
        Cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="flex items-center">
              <Tooltip title="View timesheet" arrow>
                <IconButton
                  size="small"
                  onClick={() => navigate(`/admin/timesheets/view/${item.id}`)}
                >
                  <AiFillEye className="text-primary" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit employee" arrow>
                <IconButton
                  size="small"
                  onClick={() => navigate(`/admin/timesheets/edit/${item.id}`, { state: { item } })}
                >
                  <AiFillEdit className="text-primary" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete employee" arrow>
                <IconButton
                  size="small"
                  onClick={() => setOpenConfirmationModal({ state: true, id: item.id })}
                >
                  <AiFillDelete className="text-red-600" />
                </IconButton>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [navigate]
  );

  const table = useMaterialReactTable({
    columns,
    data: employeesWithStats,
    enableRowNumbers: true,
    rowNumberDisplayMode: "original",
    enableStickyHeader: true,
    enablePagination: true,
    initialState: {
      showGlobalFilter: true,
      density: "compact",
      sorting: [{ id: "name", desc: false }],
      pagination: { pageIndex: 0, pageSize: 20 },
    },
    muiTableContainerProps: {
      sx: { maxHeight: "60vh", minHeight: "40vh", overflowY: "auto" },
    },
    state: {
      isLoading: employeesLoading,
      showProgressBars: employeesLoading,
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        if (!eventClick(event)) {
          navigate(`/admin/timesheets/view/${row.original.id}`);
        }
      },
      sx: {
        cursor: "pointer",
        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
      },
    }),
  });

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-app-surface text-app-text">
          <div className="rounded-t mb-0 px-4 py-3 border-0">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-semibold text-lg text-app-text">
                Timesheets ({employeesWithStats.length}) <RefreshData refreshData={refreshAll} />
              </h3>
              <div className="flex flex-wrap items-center gap-2">
                <Tooltip title={clockInUrl} arrow>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={copyClockInLink}
                  >
                    Copy clock-in link
                  </Button>
                </Tooltip>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  component="a"
                  href={clockInUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open kiosk
                </Button>
                <Button
                  variant="contained"
                  startIcon={<FaPlusCircle />}
                  component={Link}
                  to="/admin/timesheets/add"
                >
                  Add Employee
                </Button>
              </div>
            </div>
          </div>
          <div className="px-4 pb-4">
            <TimesheetStatCards
              employeeCount={computedSummary.employeeCount}
              clockedInCount={computedSummary.clockedInCount}
              weekMinutes={computedSummary.weekMinutes}
              unpaidMinutes={computedSummary.unpaidMinutes}
              unpaidCount={computedSummary.unpaidCount}
            />
            <Tabs value={tab} onChange={(_, value) => setTab(value)} className="mb-3">
              <Tab label="Employees" />
              <Tab label={`Working today (${workingToday.length})`} />
              <Tab label={`Time entries (${entries.length})`} />
            </Tabs>
            {tab === 0 ? (
              <MaterialReactTable table={table} />
            ) : tab === 1 ? (
              <TimesheetWorkingTodayTable rows={workingToday} isLoading={entriesLoading} />
            ) : (
              <TimesheetEntriesTable
                entries={entries}
                isLoading={entriesLoading}
                isUpdatingPayment={updatePaymentMutation.isLoading}
                onTogglePayment={(entry, paymentStatus) =>
                  updatePaymentMutation.mutate({ id: entry.id, paymentStatus })
                }
              />
            )}
          </div>
        </div>
      </div>
      {openConfirmationModal.state && (
        <DeleteModal
          open={openConfirmationModal.state}
          item="Employee"
          handleCancel={() => setOpenConfirmationModal({ state: false, id: null })}
          handleDelete={() => deleteEmployeeMutation.mutate(openConfirmationModal.id)}
        />
      )}
    </div>
  );
};

export default Timesheets;
